import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { NgSelectComponent } from "@ng-select/ng-select";
import { PopoverController } from "ionic-angular";
import * as _ from "lodash";
import { ScriptSureDrug } from "models/scriptSure/scriptsureDrug";
import {
  isOtherDose,
  OtherDose,
  ScriptSureDrugDoseResponse,
} from "models/scriptSure/scriptsureDrugDoseResponse";
import { ScriptSureDrugsRequest } from "models/scriptSure/scriptsureDrugsRequest";
import { ScriptSureDrugsResponse } from "models/scriptSure/scriptsureDrugsResponse";
import * as moment from "moment-timezone";
import { MedicationDataService } from "providers/order-data-service/medications-data-service";
import { StateManagementService } from "providers/state-management/state-management.service";
import { Observable, Subject, Subscription } from "rxjs";
import { of } from "rxjs/observable/of";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  mergeMap,
  switchMap,
  tap,
} from "rxjs/operators";
import { medicationOrderConfig } from "../../../../configs/medication-order-config";
import SortOptions from "../../../../models/content-wrapper/sort-options";
import { CreatinineData } from "../../../../models/creatinine/creatinine-data";
import { DosageTypeError } from "../../../../models/creatinine/dossage-type-error";
import CompanyResourceType from "../../../../models/resource-management/company-resource-type.enum";
import SelectFormInput from "../../../../models/SelectFormInput";
import { OrderDataService } from "../../../../providers/order-data-service/order-data-service";
import { ResourceManagementService } from "../../../../providers/resource-management/resource-management.service";
import { UtilityService } from "../../../../providers/utility-service/utility-service";
import { SelectFormInputComponent } from "../../../configurable/inputs/select-form-input/select-form-input";
import { OrderForm } from "../order-form/order-form";

type StrengthOption = (ScriptSureDrugDoseResponse | OtherDose) & {
  value: string;
};

/**
 * Generated class for the MedicationOrderFormComponent component.
 *
 * See https://angular.io/api/core/Component for more info on Angular
 * Components.
 */
@Component({
  selector: "medication-order-form",
  templateUrl: "medication-order-form.html",
})
export class MedicationOrderFormComponent
  extends OrderForm
  implements OnInit, OnDestroy
{
  protected incomingData: any = {};

  protected orderForm: FormGroup;
  private textAreaMaxLength: number = 1000;
  private shortTextMaxlength: number = 100;
  private additionalInstructionsMaxLength: number = 200;
  private startDateMin: string = "";
  private startDateMax: string = "";
  private endDateMin: string = "";
  private endDateMax: string = "";

  private medicationsOptions: any[] = [];

  private displayedIssuesOptions: any[] = [];

  private ingredients: any = FormArray;
  private latestSearchTerm: string = "";
  private creatinineData: CreatinineData;
  private creatinineSubscription$: Subscription;
  defaultStartTime: string = "";
  hideMedicationsOptions: boolean = true;
  hideIssuesOptions: boolean = true;
  dosageTypeError = DosageTypeError;
  creatinineDossageTypeError = DosageTypeError.unknown;
  prescribedTimes: Array<String>;
  showTimeRequiredMsg: boolean = false;
  selectedTimeOptions: Array<String> = [];
  containerWidth: number;
  showTimeRequiredErrorMsg: boolean = false;
  medicationOrderConfig: Object = medicationOrderConfig;
  prescribedDays: Array<Object>;
  selectedWeekOptions: Array<String> = [];
  showSelectedWeekOptions: string;
  selectedPrescribedFrequency: string;
  prescribedFrequency: Array<Object>;
  prescribedSchedule: Array<Object>;
  selectedPrescribedSchedule: string;
  disableExtraFilters: boolean = false;
  hideTimeOptions: boolean = false;
  medAdminConfig: Object;
  lodash = _;

  hidePhysiciansOptions: Boolean = true;
  searchTerm: string = "";
  selectPhysicianFlag: boolean = false;
  setPhysicianName: boolean = false;

  hideDcPhysiciansOptions: Boolean = true;
  dcSearchTerm: string = "";
  selectDcPhysicianFlag: boolean = false;
  setDcPhysicianName: boolean = false;

  private physicians: any = [];

  @Input() patientData: any;
  @Input() categoryOptions: SelectFormInput[] = [];
  @Input() issuesOptions: object[] = [];
  @Input() physicianOptions: SelectFormInput[] = [];
  @Input() physicianOrderOptions: SelectFormInput[] = [];
  @Input() administeredByOptions: SelectFormInput[] = [];
  @Input() dosageParameterOptions: SelectFormInput[] = [];
  @Input() status: any;
  @Input() showMAR: boolean;
  @Input() isOpenOrderEdit: boolean = false;
  @Input() isActiveOrderEdit: boolean = false;
  @Input() isIpad: boolean = false;
  @Output() onValidityChange: EventEmitter<any> = new EventEmitter();

  @ViewChild("dosingParameters") dosingParameters: SelectFormInputComponent;
  @ViewChild("itemContainer") teamContainer: ElementRef;

  medications$: Observable<Array<ScriptSureDrug>> = of([]);
  medicationInput$ = new Subject<string>();
  medicationsLoading = false;
  selectedNameDescription: number;
  duplicatedMed = false;
  searchSubject: Subject<string> = new Subject();
  @ViewChild("medicationNameId") medicationNameInput: NgSelectComponent;
  @ViewChild("hiddenInput") hiddenInput: {
    setFocus: () => void;
  };
  medRouteId = 0;
  strengthOptions: StrengthOption[] = [];
  selectedStrengthOption: string;
  //

  constructor(
    private formBuilder: FormBuilder,
    private orderDataService: OrderDataService,
    private popover: PopoverController,
    private resourceManagementService: ResourceManagementService,
    public utilityService: UtilityService,
    public dataService: StateManagementService,
    public medicationDataService: MedicationDataService
  ) {
    super();
    this.creatinineSubscription$ = this.orderDataService
      .getCarryThroughCreatinineData()
      .subscribe(
        (data: CreatinineData) => {
          this.creatinineData = data;
        },
        (error) => console.log(error)
      );
    this.startDateMin = moment().subtract("10", "years").format("YYYY-MM-DD");
    this.startDateMax = moment().add("10", "years").format("YYYY-MM-DD");
    this.endDateMin = moment().subtract("10", "years").format("YYYY-MM-DD");
    this.endDateMax = moment().add("10", "years").format("YYYY-MM-DD");
    this.defaultStartTime = moment().format("YYYY-MM-DD");

    this.ingredients = this.formBuilder.array([]);
    this.addIngredient();
    this.addIngredient();

    this.orderForm = this.formBuilder.group({
      categories: ["", Validators.compose([Validators.required])],
      issue: [
        "",
        Validators.compose([Validators.maxLength(this.textAreaMaxLength / 2)]),
      ],
      symptoms: [
        "",
        Validators.compose([Validators.maxLength(this.textAreaMaxLength / 2)]),
      ],
      name: [
        "",
        Validators.compose([
          Validators.required,
          Validators.maxLength(this.shortTextMaxlength),
        ]),
      ],
      compound: [false],
      doseForm: [
        "",
        Validators.compose([Validators.required, this.cannotBeNA()]),
      ],
      route: ["", Validators.compose([Validators.required, this.cannotBeNA()])],
      strength: [
        "",
        Validators.compose([Validators.required, this.cannotBeNA()]),
      ],
      frequency: [
        "",
        Validators.compose([Validators.required, this.cannotBeNA()]),
      ],
      dosage: [
        "",
        Validators.compose([Validators.required, this.cannotBeNA()]),
      ],
      opioid: [false],
      itemStartDatetime: ["", Validators.compose([Validators.required])],
      itemDiscontinueDatetime: [""],
      physician: ["", Validators.compose([Validators.required])],
      administeredBy: ["", Validators.compose([Validators.required])],
      hospicePayer: [false], //required field but it's boolean
      teachingsPerformed: [false],
      acdApproval: [false],
      description: [
        "",
        Validators.compose([Validators.maxLength(this.textAreaMaxLength)]),
      ],
      emergencyDrugPack: [false],
      gpi: [""],
      administered: [false],
      physicianOrder: ["", Validators.compose([Validators.required])],
      physicianOrderNotes: [
        "",
        Validators.compose([Validators.maxLength(this.textAreaMaxLength / 2)]),
      ],
      ingredients: this.ingredients,
      additionalInstructions: [
        "",
        Validators.compose([
          Validators.maxLength(this.additionalInstructionsMaxLength),
        ]),
      ],
      searchTerm: new FormControl(""),
      dcSearchTerm: new FormControl(""),
    });

    this.orderForm.addControl("strengthSelector", new FormControl(""));
    this.orderForm.addControl(
      "medicationSelector",
      new FormControl("", Validators.compose([Validators.required]))
    );

    this.orderForm.valueChanges.subscribe((data) => {
      this.onValidityChange.emit({
        validity: this.orderForm.valid,
        data: data,
      });
    });

    this.orderForm.get("compound").valueChanges.subscribe((data) => {
      this.creatinineDossageTypeError = DosageTypeError.unknown;
      //this.orderForm.controls['name'].setValue("");
      this.resetDosageFields();

      //reset ingredients
      let ingredientCount = 0;
      for (let i = 0; i < this.ingredients.controls.length; i++) {
        ingredientCount++;
        this.removeIngredient(0);
      }
      for (let i = 0; i < ingredientCount; i++) {
        this.addIngredient();
      }

      if (data) {
        //clear name field
        //this.orderForm.controls['name'].setValue("");

        //set all ingredients inputs to required
        for (let ingredient of this.ingredients.controls) {
          ingredient.setValidators([
            Validators.required,
            Validators.maxLength(this.shortTextMaxlength),
          ]);
          ingredient.updateValueAndValidity();
        }
      } else {
        //set all ingredients inputs to not required
        for (let ingredient of this.ingredients.controls) {
          ingredient.setValidators(null);
          ingredient.updateValueAndValidity();
        }
      }
    });

    //enforce character limit
    this.orderForm.controls["description"].valueChanges.subscribe((data) => {
      if (data && data.length > this.textAreaMaxLength) {
        this.orderForm.controls["description"].setValue(
          data.substring(0, this.textAreaMaxLength)
        );
      }
    });

    //enforce character limit
    this.orderForm.controls["doseForm"].valueChanges.subscribe((data) => {
      if (data && data.length > this.shortTextMaxlength) {
        this.orderForm.controls["doseForm"].setValue(
          data.substring(0, this.shortTextMaxlength)
        );
      }
    });

    //enforce character limit
    this.orderForm.controls["route"].valueChanges.subscribe((data) => {
      if (data && data.length > this.shortTextMaxlength) {
        this.orderForm.controls["route"].setValue(
          data.substring(0, this.shortTextMaxlength)
        );
      }
    });

    //enforce character limit
    this.orderForm.controls["strength"].valueChanges.subscribe((data) => {
      if (data && data.length > this.shortTextMaxlength) {
        this.orderForm.controls["strength"].setValue(
          data.substring(0, this.shortTextMaxlength)
        );
      }
    });

    //enforce character limit
    this.orderForm.controls["frequency"].valueChanges.subscribe((data) => {
      if (data && data.length > this.shortTextMaxlength) {
        this.orderForm.controls["frequency"].setValue(
          data.substring(0, this.shortTextMaxlength)
        );
      }
    });

    //enforce character limit
    this.orderForm.controls["dosage"].valueChanges.subscribe((data) => {
      if (data && data.length > this.shortTextMaxlength) {
        this.orderForm.controls["dosage"].setValue(
          data.substring(0, this.shortTextMaxlength)
        );
      }
    });

    //enforce startDate cannot be after endDate
    this.orderForm.controls["itemStartDatetime"].valueChanges.subscribe(
      (data) => {
        let startDate = moment(data).format("YYYY-MM-DD");
        let endValue = this.orderForm.controls["itemDiscontinueDatetime"].value;

        if (endValue.length > 0) {
          let endDate = moment(endValue).format("YYYY-MM-DD");
          if (moment(startDate).isAfter(endDate)) {
            this.orderForm.controls["itemStartDatetime"].setValue("");
          }
        }

        this.endDateMin = moment(data).format("YYYY-MM-DD");
      }
    );

    //enforce character limit
    this.orderForm.controls["physicianOrderNotes"].valueChanges.subscribe(
      (data) => {
        if (data.length > this.textAreaMaxLength / 2) {
          this.orderForm.controls["physicianOrderNotes"].setValue(
            data.substring(0, this.textAreaMaxLength / 2)
          );
        }
      }
    );

    //enforce endDate cannot be before startDate
    this.orderForm.controls["itemDiscontinueDatetime"].valueChanges.subscribe(
      (data) => {
        let startValue = this.orderForm.controls["itemStartDatetime"].value;
        let endDate = moment(data).format("YYYY-MM-DD");

        if (startValue.length > 0) {
          let startDate = moment(startValue).format("YYYY-MM-DD");
          if (moment(startDate).isAfter(endDate)) {
            this.orderForm.controls["itemDiscontinueDatetime"].setValue("");
          }
        }

        this.startDateMax = moment(data).format("YYYY-MM-DD");
      }
    );

    this.orderForm.controls["categories"].valueChanges.subscribe(() => {
      this.orderForm.controls["issue"].setValue("");
    });

    this.orderForm.controls["additionalInstructions"].valueChanges.subscribe(
      (data) => {
        if (data && data.length > this.additionalInstructionsMaxLength) {
          this.orderForm.controls["additionalInstructions"].setValue(
            data.substring(0, this.additionalInstructionsMaxLength)
          );
        }
      }
    );
  }

  ingredientsRequiredValidator(
    control: AbstractControl
  ): { [key: string]: boolean } | null {
    return this.orderForm.controls["compound"].value;
  }

  ngOnInit() {
    /*
      Populate the dosageParameters with incomingData's name -
      needs to be out of the constructor because set data()
      in OrderForm needs to fire first
    */
    // TODO BBP -- NOTE HERE
    if (this.incomingData) {
      if (this.incomingData.rmPhysician) {
        this.searchTerm = this.incomingData.rmPhysician.displayName;
      }

      // get rid of the initial ingredients that are created on page load
      if (
        this.incomingData.ingredients &&
        this.incomingData.ingredients.length > 0 &&
        this.incomingData.compound
      ) {
        this.removeIngredient(0);
        this.removeIngredient(0);

        this.incomingData.ingredients.forEach((ing) => {
          if (ing.display) {
            if (ing.id) {
              this.addIngredient(ing.display, ing.id);
            } else {
              this.addIngredient(ing.display);
            }
          }
        });

        while (this.ingredients.length < 2) {
          this.addIngredient();
        }
      }
    }

    //apply limits to prevent startDate > endDate if data exists
    let startDatetime = this.incomingData.itemStartDatetime;
    if (startDatetime) {
      this.endDateMin = moment(startDatetime).format("YYYY-MM-DD");
    }
    let endDatetime = this.incomingData.itemDiscontinueDatetime;
    if (endDatetime) {
      this.startDateMax = moment(endDatetime).format("YYYY-MM-DD");
    }

    // if discontinue make itemDiscontinueDatetime required
    if (this.status === "discontinue") {
      let discDate = this.orderForm.get("itemDiscontinueDatetime");
      discDate.setValidators([Validators.required]);
      discDate.updateValueAndValidity();
      this.orderForm.addControl(
        "dcPhysician",
        new FormControl("", Validators.required)
      );
      this.orderForm.controls["dcPhysician"].updateValueAndValidity();
    }

    this.getMedicationAdminConfig();
    this.containerWidth = this.teamContainer.nativeElement.offsetWidth;
    if (this.showMAR) {
      this.orderForm.addControl("prescribedSchedule", new FormControl(""));
      this.orderForm.controls["prescribedSchedule"].setValidators([
        Validators.required,
      ]);
      this.orderForm.controls["prescribedSchedule"].updateValueAndValidity();
      this.orderForm.addControl("prescribedTimes", new FormControl(""));
      this.orderForm.controls["prescribedTimes"].setValidators([
        Validators.required,
      ]);
      this.orderForm.controls["prescribedTimes"].updateValueAndValidity();
      this.orderForm.addControl("prescribedDays", new FormControl(""));
      this.orderForm.controls["prescribedDays"].setValidators([
        Validators.required,
      ]);
      this.orderForm.controls["prescribedDays"].updateValueAndValidity();
      this.orderForm.addControl("prescribedFrequency", new FormControl(""));
      this.orderForm.controls["prescribedFrequency"].setValidators([
        Validators.required,
      ]);
      this.orderForm.controls["prescribedFrequency"].updateValueAndValidity();
    }

    this.medications$ = this.medicationInput$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      tap(() => (this.medicationsLoading = true)),
      switchMap((term) => {
        // null -> clear, '' -> field cleared by backspace
        if (!term || term.length === 0) {
          return of([]);
        }
        return this.medicationDataService
          .getMedications(this.searchInputToScriptSureSearchRequest(term))
          .pipe(
            mergeMap((scriptSureDrugsResponse) =>
              of(scriptSureDrugsResponse.drugs)
            ),
            catchError(() => of([])), // empty list on error
            tap(() => (this.medicationsLoading = false))
          );
      })
    );

    if (this.incomingData["name"]) {
      this.setMedName(this.incomingData["name"]);
    }
  }

  // scriptsure
  searchInputToScriptSureSearchRequest(input: string): ScriptSureDrugsRequest {
    return {
      term: input,
      searchMedication: true,
      searchIndication: false,
      searchOtc: true,
      searchSupply: true,
      searchBrand: true,
      searchGeneric: true,
      includeCode: true,
      searchStatus: 0,
    };
  }

  onMedicationSelected(drug: ScriptSureDrug) {
    if (!drug) {
      return;
    }
    this.medRouteId = Number(drug.ROUTED_MED_ID);
    this.orderForm.controls["name"].setValue(drug.MED_ROUTED_MED_ID_DESC);
    this.getDrugDoses(this.medRouteId);
    if (this.selectedStrengthOption) {
      this.selectedStrengthOption = null;
      this.orderForm.controls["strength"].setValue("");
    }
  }

  onClick() {
    this.hiddenInput.setFocus();
    this.medicationNameInput.filterInput.nativeElement.focus();
  }

  get notFoundText(): string {
    return this.medicationsLoading
      ? "Searching medications..."
      : "No medications found.";
  }

  searchInputChanged(e: any) {
    let searchTerm = e.target.value;
    this.searchSubject.next(searchTerm);
  }

  onStrengthOptionSelected(event: StrengthOption) {
    if (event !== null && event !== undefined) {
      this.selectedStrengthOption = event.value;
      this.orderForm.controls["strength"].setValue(this.getStrength(event));
    }
  }

  getDrugDoses(id: number) {
    this.medicationDataService.getMedicationDoses(id).subscribe(
      (response: ScriptSureDrugDoseResponse[]) => {
        this.strengthOptions = response.map(
          this.mapStrengthOptionToIncludeValue
        );
      },
      (error) => {
        console.error("Error when searching for medication options", error);
      }
    );
  }

  mapStrengthOptionToIncludeValue(
    option: ScriptSureDrugDoseResponse | OtherDose
  ): StrengthOption {
    return {
      ...option,
      value: isOtherDose(option)
        ? option.MED_DOSAGE_FORM_DESC
        : `${option.GCN_SEQNO}`,
    };
  }

  onChangeCompound() {
    const compoundSelected = this.orderForm.get("compound").value;
    if (compoundSelected) {
      this.orderForm.removeControl("strengthSelector");
      this.orderForm.removeControl("medicationSelector");
      this.orderForm.get("strength").clearValidators();
      this.orderForm.get("name").setValue("");
    } else {
      this.orderForm.addControl("strengthSelector", new FormControl(""));
      this.orderForm.addControl(
        "medicationSelector",
        new FormControl("", Validators.compose([Validators.required]))
      );
      this.orderForm
        .get("strength")
        .setValidators(Validators.compose([Validators.required]));
      this.ingredients = this.formBuilder.array([]);
    }
  }

  setMedName(drugName: string) {
    if (!this.orderForm.get("compound").value) {
      this.orderForm.addControl("strengthSelector", new FormControl(""));
      this.orderForm.addControl(
        "medicationSelector",
        new FormControl("", Validators.compose([Validators.required]))
      );
      this.orderForm
        .get("strength")
        .setValidators(Validators.compose([Validators.required]));
      this.ingredients = this.formBuilder.array([]);

      this.medicationDataService
        .getMedications(this.searchInputToScriptSureSearchRequest(drugName))
        .subscribe(
          (response: ScriptSureDrugsResponse) => {
            this.medicationInput$.next(drugName);
            const med = response.drugs.find(
              (med) => med.MED_ROUTED_MED_ID_DESC === drugName
            );
            if (med) {
              this.selectedNameDescription = med.MED_NAME_ID;
              this.medRouteId = Number(med.ROUTED_MED_ID);
              this.orderForm.controls["name"].setValue(
                med.MED_ROUTED_MED_ID_DESC
              );

              this.medicationDataService
                .getMedicationDoses(med.ROUTED_MED_ID)
                .subscribe(
                  (response: ScriptSureDrugDoseResponse[]) => {
                    this.strengthOptions = response.map(
                      this.mapStrengthOptionToIncludeValue
                    );
                    const strengthScriptSure = this.strengthOptions.find(
                      (strength) => {
                        if (isOtherDose(strength)) {
                          return false; // No other doses on page load.
                        } else if (
                          strength.MED_STRENGTH &&
                          strength.MED_STRENGTH_UOM
                        ) {
                          return (
                            `${strength.MED_STRENGTH} ${strength.MED_STRENGTH_UOM}` ===
                            this.incomingData["strength"]
                          );
                        } else {
                          return (
                            strength.MED_DOSAGE_FORM_DESC ===
                            this.incomingData["strength"]
                          );
                        }
                      }
                    );
                    this.orderForm.controls["strength"].setValue(
                      this.getStrength(strengthScriptSure)
                    );
                    if (isOtherDose(strengthScriptSure)) {
                      this.selectedStrengthOption =
                        strengthScriptSure.MED_MEDID_DESC;
                    } else {
                      this.selectedStrengthOption = `${strengthScriptSure.GCN_SEQNO}`;
                    }
                  },
                  (error) => {
                    console.error(
                      "Error when searching for medication options",
                      error
                    );
                  }
                );
            } else {
              this.medications$ = of([this.getMedFromName(drugName)]);
              this.strengthOptions = [
                this.getStrengthFromName(this.incomingData["strength"]),
              ].map(this.mapStrengthOptionToIncludeValue);
              this.orderForm.controls["name"].setValue(drugName);
              this.orderForm
                .get("strength")
                .setValue(this.incomingData["strength"]);
              this.orderForm.get("medicationSelector").setValue(0);
              this.orderForm.get("strengthSelector").setValue(0);
              this.selectedStrengthOption = "0";
            }
          },
          (error) => {
            console.error("Error when searching for medication options", error);
          }
        );
    } else {
      this.orderForm.removeControl("strengthSelector");
      this.orderForm.removeControl("medicationSelector");
      this.orderForm.get("strength").clearValidators();
    }
  }

  // end scriptsure

  addIngredient(
    defaultVal: string = "",
    id: number = null,
    creatinineCheck: boolean = false
  ): void {
    this.ingredients.push(
      this.createIngredient(defaultVal, id, creatinineCheck)
    );
  }

  createIngredient(
    defaultVal: string = "",
    id: number = null,
    creatinineCheck: boolean = false
  ): FormGroup {
    return this.formBuilder.group({
      display: [defaultVal, Validators.maxLength(this.shortTextMaxlength)],
      id: [id],
      creatinineCheck: [creatinineCheck],
    });
  }

  removeIngredient(index): void {
    this.ingredients.removeAt(index);
  }

  getStrength(strengthScriptSure: StrengthOption) {
    if (isOtherDose(strengthScriptSure)) {
      return strengthScriptSure.otherDescription;
    }
    if (strengthScriptSure) {
      const { MED_STRENGTH, MED_STRENGTH_UOM, MED_DOSAGE_FORM_DESC } =
        strengthScriptSure;
      if (
        MED_STRENGTH &&
        MED_STRENGTH_UOM &&
        MED_STRENGTH !== "" &&
        MED_STRENGTH_UOM !== ""
      ) {
        return `${strengthScriptSure.MED_STRENGTH} ${strengthScriptSure.MED_STRENGTH_UOM}`;
      }
      return MED_DOSAGE_FORM_DESC;
    } else {
      return this.incomingData["strength"];
    }
  }

  addOtherStrength = (addTagText: string) => {
    this.strengthOptions = [
      ...this.strengthOptions,
      {
        MED_MEDID_DESC: addTagText,
        otherDescription: addTagText,
        MED_DOSAGE_FORM_DESC: undefined,
        ndc: undefined,
        rxNorm: undefined,
        value: addTagText,
      },
    ];
  };

  /* ---- issue autocomplete ---- */

  searchIssues(searchTerm: string) {
    if (this.orderForm.controls["categories"].value === "") {
      //default search if no category is selected
      if (
        this.displayedIssuesOptions.filter(
          (issue) => issue.description === searchTerm
        ).length === 0 &&
        searchTerm !== ""
      ) {
        this.hideIssuesOptions = false;
        this.displayedIssuesOptions = this.issuesOptions.filter((issue) =>
          issue["description"].toLowerCase().includes(searchTerm.toLowerCase())
        );
      } else {
        this.hideIssuesOptions = true;
      }
    } else {
      let category = this.orderForm.controls["categories"].value;
      if (
        this.displayedIssuesOptions.filter(
          (issue) => issue.description === searchTerm
        ).length === 0 &&
        searchTerm !== ""
      ) {
        this.hideIssuesOptions = false;
        this.displayedIssuesOptions = this.issuesOptions.filter((issue) => {
          let categoryObj = this.categoryOptions.find((option) => {
            return option.value === category;
          });
          return (
            issue["description"]
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) &&
            issue["lookups"]["sectionTypeLookupCode"][0]["value"] ===
              categoryObj.display
          );
        });
      } else {
        this.hideIssuesOptions = true;
      }
    }
  }

  setIssue(issue: any) {
    this.orderForm.controls["issue"].setValue(issue.description);
    this.hideIssuesOptions = true;
  }

  setIngredientName(medication, index) {
    let birthdate = this.patientData.patientChart.patientBirthdate;
    let weight = this.patientData.patientChart.currentWeightKgs;
    let newValue = {
      display: medication.name,
      id: null,
      creatinineCheck: false,
    };
    this.orderDataService
      .getMedicationInformation(
        medication.mediSpanId,
        birthdate,
        weight,
        this.creatinineData
      )
      .subscribe(
        (data) => {
          // console.log("New ingredient does not require creatinine clearance");
          this.creatinineDossageTypeError = DosageTypeError.unknown;
          this.orderForm.controls["ingredients"]["controls"][index].setValue(
            newValue
          );
        },
        (err) => {
          this.creatinineDossageTypeError =
            this.stablishTypeOfMediSpanError(err);
          if (this.isErrorCreatinineRelated(err)) {
            newValue.creatinineCheck = true;
            this.orderForm.controls["ingredients"]["controls"][index].setValue(
              newValue
            );
          }
        }
      );
  }

  /* ---- meds autocomplete ---- */

  resetDosageFields() {
    if (this.orderForm) {
      this.orderForm.controls["dosage"].setValue("");
      this.orderForm.controls["frequency"].setValue("");
      this.orderForm.controls["strength"].setValue("");
      this.orderForm.controls["route"].setValue("");
      this.orderForm.controls["doseForm"].setValue("");

      // need to reset dosage parameter options if the user selects a compound
      if (this.dosageParameterOptions.length > 0) {
        this.dosageParameterOptions = [];
      }
    }
  }

  stablishTypeOfMediSpanError(error: any): DosageTypeError {
    // todo make here error controls for creatinine clearance and serum creatinine.
    const {
      error: { message },
    } = error;
    const [creatinineRegex, weightRegex, birthDateRegex] = [
      /Creatinine/g,
      /weight/gi,
      /yyyy-MM-dd/gi,
    ];
    return creatinineRegex.test(message)
      ? DosageTypeError.creatinine
      : weightRegex.test(message)
      ? DosageTypeError.weight
      : birthDateRegex.test(message)
      ? DosageTypeError.patientBirthdate
      : DosageTypeError.unknown;
  }

  isErrorCreatinineRelated(err) {
    let coreError = err.error;
    const creatinineCheck = /Creatinine/g;
    return creatinineCheck.test(coreError.message);
  }

  cannotBeNA() {
    //validator used to ensure that some fields cannot be 'N/A'
    const naRegex = /^n\/?a$/i;
    return (control: AbstractControl): { [key: string]: any } | null => {
      const isNA = naRegex.test(control.value);
      return isNA ? { cannotBeNA: { value: control.value } } : null;
    };
  }

  getMedicationAdminConfig() {
    this.orderDataService.getMedicationAdminConfig().subscribe((data) => {
      this.medAdminConfig = data["medAdminConfig"];
      this.makeAdministrationOptions(data["medAdminConfig"]);
      if (this.status === "edit" || this.status === "discontinue") {
        this.setAdministration({
          schedule: this.incomingData.prescribedSchedule,
          selectedStandardTimesArray: this.incomingData.prescribedTimes,
        });
      }
    });
  }

  makeAdministrationOptions(options) {
    if (!_.isEmpty(options)) {
      this.prescribedSchedule = options.map((ele) => {
        return { name: ele["schedule"], value: ele };
      });
    }
  }

  makePrescribedFrequencyOptions(options) {
    if (!_.isEmpty(options)) {
      this.prescribedFrequency = options.map((ele) => {
        return {
          name: ele.name,
          value: ele.value,
        };
      });
      //By default Set iT to 'Daily' option
      this.setPrescribedFrequency("Daily");
    }
  }

  makeDropdownTimeOptions(options, selectedOptions?) {
    if (!_.isEmpty(options)) {
      this.prescribedTimes = options.map((ele) => {
        return {
          name: ele,
          value: ele,
          checked: selectedOptions ? selectedOptions.includes(ele) : true,
        };
      });
    }
    this.setOptions(
      this.prescribedTimes,
      "prescribedTimes",
      "selectedTimeOptions"
    );
  }

  setOptions(options, key, selectedItems) {
    this[selectedItems] = _.without(
      _.map(options, function (ele) {
        return ele.checked ? ele.name : null;
      }),
      null
    );
    if (key === "prescribedDays") {
      this.showSelectedWeekOptions = _.without(
        _.map(options, function (ele) {
          return ele.checked ? ele.value : null;
        }),
        null
      );
    }
    this.orderForm.controls[key].setValue(this[selectedItems]);
  }

  makeWeekDaysDropdownOptions(options, key, selectedDays?) {
    if (!_.isEmpty(options)) {
      this[key] = options.map((ele) => {
        return {
          name: ele.name,
          value: ele.value,
          checked: selectedDays ? selectedDays.includes(ele.name) : true,
        };
      });
    }
    if (key === "prescribedDays") {
      this.setOptions(this[key], key, "selectedWeekOptions");
    }
  }

  selectOptions(ev, key, isMultiSelect) {
    if (
      (this.disableExtraFilters &&
        (key === "prescribedDays" || key === "prescribedFrequency")) ||
      this.status === "discontinue"
    ) {
      return;
    }
    if (!_.isEmpty(this[key])) {
      const searchable = false;
      let options = this[key].map((a) => ({ ...a }));
      const popover = this.popover.create(
        "CareTeamsSelectPage",
        {
          options,
          searchable,
          isMultiSelect: isMultiSelect,
          isAutoWidth: true,
          noSort: true,
          containerWidth: this.containerWidth,
        },
        { cssClass: "multiple-select-custom-popover" }
      );
      popover.onDidDismiss((data) => {
        if (data) {
          switch (key) {
            case "prescribedDays":
              this[key] = data.options;
              this.setOptions(data.options, key, "selectedWeekOptions");
              break;
            case "prescribedFrequency":
              this.setPrescribedFrequency(data);
              break;
            case "prescribedTimes":
              this[key] = data.options;
              this.setOptions(
                data.options,
                "prescribedTimes",
                "selectedTimeOptions"
              );
              break;
            case "prescribedSchedule":
              this.setAdministration(data);
              break;
          }
        }
      });
      popover.present({ ev });
    }
  }

  setPrescribedFrequency(data) {
    this.selectedPrescribedFrequency = data;
    this.orderForm.controls["prescribedFrequency"].setValue(
      this.selectedPrescribedFrequency
    );
    this.orderForm.controls["prescribedFrequency"].updateValueAndValidity();
  }

  setAdministration(option) {
    this.selectedPrescribedSchedule = option.schedule;
    this.orderForm.controls["prescribedSchedule"].setValue(
      this.selectedPrescribedSchedule
    );
    if (option.schedule === "NA") {
      this.hideTimeOptions = true;
      this.orderForm.controls["prescribedTimes"].setValue(null);
      this.orderForm.controls["prescribedTimes"].setValidators(null);
      this.orderForm.controls["prescribedTimes"].updateValueAndValidity();
    } else {
      this.hideTimeOptions = false;
      this.orderForm.controls["prescribedTimes"].setValidators([
        Validators.required,
      ]);
      this.orderForm.controls["prescribedTimes"].updateValueAndValidity();
      if (this.status === "edit" || this.status === "discontinue") {
        let selectedSchedule = _.find(this.medAdminConfig, [
          "schedule",
          option.schedule,
        ]);
        this.makeDropdownTimeOptions(
          selectedSchedule.standardTimesArray,
          option.selectedStandardTimesArray
        );
      } else {
        this.makeDropdownTimeOptions(option.standardTimesArray);
      }
    }
    if (
      option.schedule === "q48h" ||
      option.schedule === "q72h" ||
      option.schedule === "NA"
    ) {
      this.disableExtraFilters = true;
      this.orderForm.controls["prescribedFrequency"].setValue(null);
      this.orderForm.controls["prescribedFrequency"].setValidators(null);
      this.orderForm.controls["prescribedFrequency"].updateValueAndValidity();
      this.orderForm.controls["prescribedDays"].setValue(null);
      this.orderForm.controls["prescribedDays"].setValidators(null);
      this.orderForm.controls["prescribedDays"].updateValueAndValidity();
    } else {
      this.disableExtraFilters = false;
      this.orderForm.controls["prescribedFrequency"].setValidators([
        Validators.required,
      ]);
      this.orderForm.controls["prescribedFrequency"].updateValueAndValidity();
      this.orderForm.controls["prescribedDays"].setValidators([
        Validators.required,
      ]);
      this.orderForm.controls["prescribedDays"].updateValueAndValidity();
      if (this.status === "edit" || this.status === "discontinue") {
        this.makeWeekDaysDropdownOptions(
          this.medicationOrderConfig["weekDays"],
          "prescribedDays",
          this.incomingData.prescribedDays
        );
        this.makePrescribedFrequencyOptions([
          {
            name: this.incomingData.prescribedFrequency,
            value: this.incomingData.prescribedFrequency,
          },
        ]);
      } else {
        this.makeWeekDaysDropdownOptions(
          this.medicationOrderConfig["weekDays"],
          "prescribedDays"
        );
        this.makePrescribedFrequencyOptions(
          this.medicationOrderConfig["prescribedFrequency"]
        );
      }
    }
  }

  /**
   * @param searchTerm user enters to search the physician, when the term length is > 2 at that time call that getPhysicians api
   */
  searchPhysicians(searchTerm: string) {
    if (searchTerm !== "") {
      this.hidePhysiciansOptions = false;
      if (searchTerm.length > 2) {
        this.getPhysicians(searchTerm);
      }
    }
  }

  /**
   * @param searchTerm search term to get physicians
   */
  getPhysicians(searchTerm) {
    if (!this.selectPhysicianFlag) {
      this.selectPhysicianFlag = false;
      this.setPhysicianName = true;
      this.resourceManagementService
        .getCompanyResourceSummaries(
          0,
          10000,
          searchTerm,
          new SortOptions("last_name", "asc"),
          CompanyResourceType.PROVIDER
        )
        .subscribe(
          (response) => {
            this.physicians = [];
            let providers = _.filter(response.content, (x) => x.externalId); // valid external Id.
            this.physicians = this.physicians.concat(providers);
          },
          (err) => {
            this.utilityService.presentErrorToast(
              err,
              "Failed to Load Providers"
            );
          }
        );
    } else {
      this.selectPhysicianFlag = false;
    }
  }

  setPhysician(physician: any) {
    this.selectPhysicianFlag = true;
    this.setPhysicianName = false;
    this.hidePhysiciansOptions = true;
    this.orderForm.controls["physician"].setValue(physician);
    this.orderForm.controls["searchTerm"].setValue(physician.displayName);
  }

  /**
   * @param searchTerm user enters to search the physician, when the term length is > 2 at that time call that getPhysicians api
   */
  searchDcPhysicians(searchTerm: string) {
    if (searchTerm !== "") {
      this.hideDcPhysiciansOptions = false;
      if (searchTerm.length > 2) {
        this.getDcPhysicians(searchTerm);
      }
    }
  }

  /**
   * @param searchTerm search term to get physicians
   */
  getDcPhysicians(searchTerm) {
    if (!this.selectDcPhysicianFlag) {
      this.selectDcPhysicianFlag = false;
      this.setDcPhysicianName = true;
      this.resourceManagementService
        .getCompanyResourceSummaries(
          0,
          10000,
          searchTerm,
          new SortOptions("last_name", "asc"),
          CompanyResourceType.PROVIDER
        )
        .subscribe(
          (response) => {
            this.physicians = [];
            let providers = _.filter(response.content, (x) => x.externalId); // valid external Id.
            this.physicians = this.physicians.concat(providers);
          },
          (err) => {
            this.utilityService.presentErrorToast(
              err,
              "Failed to Load Providers"
            );
          }
        );
    } else {
      this.selectDcPhysicianFlag = false;
    }
  }

  setDcPhysician(physician: any) {
    this.selectDcPhysicianFlag = true;
    this.setDcPhysicianName = false;
    this.hideDcPhysiciansOptions = true;
    this.orderForm.controls["dcPhysician"].setValue(physician);
    this.orderForm.controls["dcSearchTerm"].setValue(physician.displayName);
  }

  ngOnDestroy(): void {
    this.creatinineSubscription$.unsubscribe();
  }

  onClear() {
    this.orderForm.controls["strength"].setValue("");
    this.orderForm.controls["strengthSelector"].setValue("");
  }

  getMedFromName(name: string): ScriptSureDrug {
    const med: ScriptSureDrug = {
      DrugGroup: "",
      FormularyStatus: "",
      GCN_SEQNO: "",
      GenericName: "",
      MED_NAME_ID: 0,
      MED_NAME_TYPE_CD: "",
      MED_ROUTED_MED_ID_DESC: "",
      MED_ROUTE_ID: 0,
      MED_STATUS_CD: "",
      Pharmacology: "",
      ROUTED_MED_ID: 0,
      TypeId: "",
      multipletype: "",
      offerid: "",
    };
    med.MED_ROUTED_MED_ID_DESC = name;
    med.MED_NAME_ID = 0;
    med.ROUTED_MED_ID = 0;
    return med;
  }

  getStrengthFromName(name: string): ScriptSureDrugDoseResponse {
    const strength: ScriptSureDrugDoseResponse = {
      GCN_SEQNO: 0,
      MED_DOSAGE_FORM_DESC: "",
      MED_MEDID_DESC: "",
      MED_NAME_TYPE_CD: "",
      MED_REF_DEA_CD: "",
      MED_REF_FED_LEGEND_IND: "",
      MED_REF_GEN_DRUG_NAME_CD: "",
      MED_ROUTED_MED_ID_DESC: "",
      MED_STATUS_CD: "",
      MED_STRENGTH: "",
      MED_STRENGTH_UOM: "",
      ROUTED_MED_ID: 0,
      epn: "",
      imk_preferred_ind: "",
      med_status_cd: "",
      ndc: "",
      rxNorm: "",
      rxNormQualifier: "",
    };
    strength.MED_MEDID_DESC = name;
    strength.GCN_SEQNO = 0;
    return strength;
  }
}



// WEBPACK FOOTER //
// ./src/components/orders/forms/medication-order-form/medication-order-form.ts